/**
 * @fileoverview Custom pipeline field badge (MOR-948)
 *
 * How a firm's custom field value reads on a pipeline card, a list row and
 * the referrer card — one component so the three can never drift.
 */

import type { BadgeColor } from "@/ui/primitives/badge";
import { Badge } from "@/ui/primitives/badge";
import { cn } from "@/utils/misc";
import {
  type CustomFieldDef,
  fieldIcon,
  formatCustomFieldValue,
} from "./custom-fields";

/**
 * A field's value as it appears on a card, row or contact panel: the
 * field's icon and colour, then the formatted value. The field name rides
 * in the tooltip so a dense card stays readable.
 */
export function CustomFieldBadge({
  field,
  value,
  showName = false,
  className,
}: {
  field: CustomFieldDef;
  value: string;
  /** Prefix the value with the field name (used where space allows). */
  showName?: boolean;
  className?: string;
}) {
  const Icon = fieldIcon(field.icon);
  return (
    <Badge
      variant="soft"
      color={field.color as BadgeColor}
      size="sm"
      className={cn("gap-1", className)}
      title={`${field.name}: ${formatCustomFieldValue(field.type, value)}`}
    >
      <Icon className="size-3 shrink-0" />
      {showName && <span className="opacity-70">{field.name}</span>}
      <span className="tabular-nums">
        {formatCustomFieldValue(field.type, value)}
      </span>
    </Badge>
  );
}
